import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  CircularProgress,
  Divider,
  Grid,
  Alert,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  CalendarToday as DateIcon,
  AttachMoney as MoneyIcon,
  Description as FileIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

const PublicJobOffer = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const db = searchParams.get('db');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offer, setOffer] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchOffer = async () => {
      if (!token || !db) {
        setError('Invalid offer link');
        setLoading(false);
        return;
      }

      try {
        // Use absolute path for public API if not proxied, or relative if proxied
        const response = await axios.get(`/api/public/transition/offer/${token}?db=${db}`);
        if (response.data.success) {
          setOffer(response.data.data);
        } else {
          setError(response.data.message || 'Failed to load offer');
        }
      } catch (err) {
        console.error('Error fetching offer:', err);
        setError(err.response?.data?.message || 'Failed to load offer details');
      } finally {
        setLoading(false);
      }
    };

    fetchOffer();
  }, [token, db]);

  const handleAction = async (action) => {
    const result = await Swal.fire({
      title: action === 'ACCEPT' ? 'Accept Job Offer?' : 'Decline Job Offer?',
      text: action === 'ACCEPT' 
        ? 'You are about to accept this job offer. We are excited to have you!' 
        : 'Are you sure you want to decline this offer?',
      icon: action === 'ACCEPT' ? 'success' : 'warning',
      showCancelButton: true,
      confirmButtonText: action === 'ACCEPT' ? 'Yes, Accept Offer' : 'Yes, Decline',
      confirmButtonColor: action === 'ACCEPT' ? '#4caf50' : '#f44336',
      cancelButtonText: 'Cancel',
      input: action === 'REJECT' ? 'textarea' : undefined,
      inputPlaceholder: action === 'REJECT' ? 'Reason for declining (optional)...' : undefined
    });

    if (result.isConfirmed) {
      setActionLoading(true);
      try {
        const response = await axios.post(`/api/public/transition/offer/${token}/respond?db=${db}`, {
          action,
          reason: result.value
        });

        if (response.data.success) {
          Swal.fire({
            title: action === 'ACCEPT' ? 'Offer Accepted!' : 'Offer Declined',
            text: action === 'ACCEPT' 
              ? 'Thank you for accepting the offer! HR has been notified.' 
              : 'Thank you for your response.',
            icon: 'success'
          });
          
          // Update local state
          setOffer(prev => ({
            ...prev,
            status: action === 'ACCEPT' ? 'ACCEPTED' : 'REJECTED'
          }));
        }
      } catch (err) {
        console.error('Error responding to offer:', err);
        Swal.fire('Error', err.response?.data?.message || 'Failed to submit response', 'error');
      } finally {
        setActionLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!offer) return null;

  const isPending = offer.status === 'PENDING';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 6 }}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
              Job Offer
            </Typography>
            <Typography variant="h6" color="textSecondary">
              {offer.jobTitle}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Chip 
                label={offer.status} 
                color={offer.status === 'ACCEPTED' ? 'success' : offer.status === 'REJECTED' ? 'error' : 'warning'}
                variant={offer.status === 'PENDING' ? 'outlined' : 'filled'}
              />
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Candidate Details */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Dear {offer.candidateName},
              </Typography>
              <Typography variant="body1" paragraph>
                We are pleased to offer you the position of <strong>{offer.jobTitle}</strong>. 
                We were impressed with your qualifications and believe you will be a great asset to our team.
              </Typography>
            </Grid>
          </Grid>

          {/* Offer Details Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <MoneyIcon color="action" />
                    <Typography variant="subtitle2" color="textSecondary">
                      Offered Salary
                    </Typography>
                  </Box>
                  <Typography variant="h6">
                    ₹{offer.offeredSalary?.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DateIcon color="action" />
                    <Typography variant="subtitle2" color="textSecondary">
                      Joining Date
                    </Typography>
                  </Box>
                  <Typography variant="h6">
                    {new Date(offer.joiningDate).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Expiry Warning */}
          {isPending && (
            <Alert severity="info" sx={{ mb: 4 }}>
              This offer expires on <strong>{new Date(offer.expiryDate).toLocaleDateString()}</strong>.
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            {offer.offerLetterUrl && (
              <Button
                variant="outlined"
                startIcon={<FileIcon />}
                href={offer.offerLetterUrl}
                target="_blank"
                sx={{ mb: 2 }}
              >
                View Detailed Offer Letter
              </Button>
            )}

            {isPending ? (
              <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<CheckIcon />}
                  onClick={() => handleAction('ACCEPT')}
                  disabled={actionLoading}
                  sx={{ minWidth: 150 }}
                >
                  Accept Offer
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<CancelIcon />}
                  onClick={() => handleAction('REJECT')}
                  disabled={actionLoading}
                  sx={{ minWidth: 150 }}
                >
                  Decline Offer
                </Button>
              </Box>
            ) : (
              <Alert severity={offer.status === 'ACCEPTED' ? 'success' : 'error'} sx={{ width: '100%' }}>
                You have {offer.status.toLowerCase()} this offer.
              </Alert>
            )}
          </Box>

        </Paper>
      </Container>
    </Box>
  );
};

export default PublicJobOffer;
